import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { useCart } from "@/contexts/CartContext";
import { CheckCircle, Copy, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

// Displays the post-checkout confirmation. For Bank Transfer orders it also
// shows the merchant bank details + payment reference + amount so the customer
// has everything they need in one place. For COD orders the same page falls
// back to a plain success view (no bank block).
export default function OrderConfirmationPage() {
  const { id } = useParams();
  const { clear } = useCart();
  const [order, setOrder] = useState(null);
  const [bank, setBank] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await api.get(`/orders/public/${id}`);
        setOrder(data.order);
        setBank(data.bank_details || null);
        clear();
      } catch (e) {
        // fall through to error state
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line
  }, [id]);

  const copy = (value, label) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  if (loading) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="order-confirmation-loading">
        <div className="animate-pulse w-16 h-16 rounded-full border-4 border-[color:var(--vt-orange)] border-t-transparent mx-auto mb-6" />
        <p className="text-neutral-500">Loading your order…</p>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="order-confirmation-error">
        <h1 className="font-display font-black text-4xl tracking-tighter mb-4">Order not found</h1>
        <p className="text-neutral-500 mb-6">We couldn't locate this order. Please check the link or contact info@veloteka.de.</p>
        <Link to="/shop" className="vt-btn-orange">Continue shopping</Link>
      </div>
    );
  }

  const isBank = order.payment_method === "bank_transfer";

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="order-confirmation-page">
      <div className="text-center mb-10">
        <CheckCircle size={72} weight="duotone" className="text-green-600 mx-auto mb-4" />
        <h1 className="font-display font-black text-5xl tracking-tighter mb-2">Order received</h1>
        <p className="text-neutral-600">
          Order <span className="font-bold text-black" data-testid="order-number">{order.order_number}</span>
          {isBank && (
            <> · Status: <span className="text-[#B45309] font-bold" data-testid="order-status-label">Waiting for Bank Transfer</span></>
          )}
        </p>
      </div>

      {isBank && (
        <div className="border border-neutral-200 bg-neutral-50 p-6 mb-8" data-testid="bank-transfer-details">
          <div className="bg-black text-[color:var(--vt-orange)] text-xs font-black uppercase tracking-widest text-center py-3 -mx-6 -mt-6 mb-6">
            Bank Transfer Details
          </div>
          <p className="text-sm text-neutral-700 mb-6">
            Please transfer <span className="font-bold text-black">€{Number(order.total).toFixed(2)}</span> to the account below and quote the payment reference. Once the funds land (usually 1–2 business days) we'll process and ship your order. A copy of these details has been emailed to you.
          </p>

          <dl className="divide-y divide-neutral-200 mb-6">
            <BankRow label="Beneficiary" value={bank?.company_name} testid="bank-company" onCopy={copy} />
            <BankRow label="Bank" value={bank?.bank_name} testid="bank-name" onCopy={copy} />
            <BankRow label="IBAN" value={bank?.iban} testid="bank-iban" mono onCopy={copy} />
            {bank?.swift && <BankRow label="BIC / SWIFT" value={bank.swift} testid="bank-swift" mono onCopy={copy} />}
          </dl>

          <div className="bg-yellow-50 border border-yellow-200 p-4 mb-4">
            <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1">Payment Reference (must include)</div>
            <div className="flex items-center justify-between gap-3">
              <div className="font-mono font-black text-lg" data-testid="payment-reference">{order.order_number}</div>
              <button className="text-xs uppercase tracking-widest bg-black text-white px-3 py-1.5 hover:bg-neutral-800"
                onClick={() => copy(order.order_number, "Payment reference")}
                data-testid="copy-reference-btn">
                <Copy size={12} className="inline mr-1" /> Copy
              </button>
            </div>
            <div className="text-xs text-neutral-500 uppercase tracking-wider mt-4 mb-1">Total Amount</div>
            <div className="flex items-center justify-between gap-3">
              <div className="font-black text-2xl text-[color:var(--vt-orange)]" data-testid="payment-amount">€{Number(order.total).toFixed(2)}</div>
              <button className="text-xs uppercase tracking-widest bg-black text-white px-3 py-1.5 hover:bg-neutral-800"
                onClick={() => copy(Number(order.total).toFixed(2), "Amount")}
                data-testid="copy-amount-btn">
                <Copy size={12} className="inline mr-1" /> Copy
              </button>
            </div>
          </div>

          <p className="text-xs text-neutral-500">
            Always include your Payment Reference so we can match your transfer to this order. Questions? Reply to your confirmation email or contact <a href="mailto:info@veloteka.de" className="underline">info@veloteka.de</a>.
          </p>
        </div>
      )}

      <div className="border border-neutral-200 p-6 mb-8" data-testid="order-summary">
        <h2 className="font-display font-bold text-xl mb-4">Order summary</h2>
        <div className="space-y-2 text-sm">
          {(order.items || []).map((it, idx) => (
            <div key={idx} className="flex justify-between">
              <span className="truncate">{it.name} × {it.quantity}</span>
              <span className="ml-2 font-semibold">€{Number(it.line_total).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="pt-4 mt-4 border-t border-neutral-200 flex justify-between font-black text-2xl">
          <span>Total</span><span>€{Number(order.total).toFixed(2)}</span>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link to="/account" className="vt-btn-orange" data-testid="view-account-btn">View my orders <ArrowRight size={14} className="inline ml-1" /></Link>
        <Link to="/shop" className="vt-btn-outline" data-testid="continue-shopping-btn">Continue shopping</Link>
      </div>
    </div>
  );
}

function BankRow({ label, value, testid, mono, onCopy }) {
  return (
    <div className="flex items-center justify-between py-3 gap-4">
      <div className="text-xs uppercase tracking-wider text-neutral-500 shrink-0">{label}</div>
      <div className="flex items-center gap-2 min-w-0">
        <span className={"truncate " + (mono ? "font-mono font-bold" : "font-semibold")} data-testid={testid}>{value || "—"}</span>
        {value && (
          <button className="text-neutral-400 hover:text-black shrink-0" onClick={() => onCopy(value, label)} title={`Copy ${label}`} data-testid={`${testid}-copy`}>
            <Copy size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
